import React, { useMemo } from 'react';
import T from 'prop-types';
import cx from 'classnames';

import { renderRowCells, skeletonRows } from './TableCells';
import { SortableTable } from '../../../../../components/SortableTable';
import ErrorStateWithReload from '../../../../../components/ErrorStateWithReload';
import DiverseSelection from '../../../../../components/Checkbox/DiverseSelection';
import { staticColumns, SELECTION_KIND } from '../../../configs/custom-exports-config';

const renderEmptyTableCell = (message) => (
  <div className="exports-table-empty-state">
    <p className="text-14-150 text-secondary">{message}</p>
  </div>
);
const emptyTableCell = renderEmptyTableCell('Create your own custom export to get started');
const emptyScheduledTableCell = renderEmptyTableCell('No scheduled exports available');

const makePreferences = ({ noneDisabled }) => [
  { value: SELECTION_KIND.all, label: 'All' },
  { value: SELECTION_KIND.none, label: 'None', disabled: noneDisabled },
];

const ExportsTable = ({
  tableData,
  tableActions,
  selectedExports,
  settings,
  initialRowsLimit,
  filteredExportsType,
  onSelectedExportsChange,
  onSortChange,
}) => {
  const selectedExportsIds = useMemo(
    () => selectedExports.map((item) => item.id),
    [selectedExports]
  );

  const columns = useMemo(() => {
    const availableExports = tableData.filter(({ state }) => state === 'processed');
    const preferences = makePreferences({ noneDisabled: !selectedExports.length });

    const checkboxColumn = {
      dataField: 'checkbox',
      text: '',
      className: 'checkbox-cell',
      element: (
        <DiverseSelection
          menuOptions={preferences}
          selected={selectedExports}
          selectableItems={availableExports}
          itemKey="id"
          menuPortal
          menuClassName="custom-export-diverse-selection"
          onChange={onSelectedExportsChange}
        />
      ),
    };
    return [checkboxColumn, ...staticColumns];
  }, [tableData, selectedExports]);

  const rows = useMemo(
    () => tableData.map((item) => renderRowCells(item, { selectedExportsIds }, tableActions)),
    [tableData, tableActions, selectedExportsIds]
  );

  if (settings.error) return <ErrorStateWithReload />;

  return (
    <SortableTable
      tableClassName={cx(
        'table-default table-default-max-padding sticky-table-header exports-table'
      )}
      defaultSortBy="created_at"
      columns={columns}
      rows={settings.pending ? skeletonRows : rows}
      initialRowsLimit={initialRowsLimit}
      showMoreCellColSpan={columns.length}
      emptyStateText={
        filteredExportsType === 'scheduled' ? emptyScheduledTableCell : emptyTableCell
      }
      hasShowMoreCell={false}
      onSortEnd={onSortChange}
    />
  );
};

ExportsTable.propTypes = {
  tableData: T.array.isRequired,
  tableActions: T.object.isRequired,
  selectedExports: T.array.isRequired,
  settings: T.object.isRequired,
  initialRowsLimit: T.number,
  filteredExportsType: T.oneOf(['all', 'scheduled']),
  onSelectedExportsChange: T.func.isRequired,
  onSortChange: T.func.isRequired,
};

export default ExportsTable;
